'use client';

import Link from 'next/link';

import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@kit/ui/input-group';
import { Trans } from '@kit/ui/trans';

import { PasswordSignInSchema } from '../schemas/password-sign-in.schema';
import { PasswordInput } from './password-input';

export function PasswordSignInForm({
  onSubmit,
  captchaLoading = false,
  loading = false,
  redirecting = false,
}: {
  onSubmit: (params: z.infer<typeof PasswordSignInSchema>) => unknown;
  captchaLoading: boolean;
  loading: boolean;
  redirecting: boolean;
}) {
  const { t } = useTranslation('auth');

  const form = useForm({
    resolver: zodResolver(PasswordSignInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  return (
    <Form {...form}>
      {/* Zod owns validation here too, so the browser's bubble does not get to
          pre-empt the form's own inline messages. method="post" keeps a
          pre-hydration native submit from putting the password in the URL. */}
      <form
        noValidate
        method={'post'}
        className={'flex w-full flex-col gap-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className={'flex flex-col space-y-2.5'}>
          <FormField
            control={form.control}
            name={'email'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <InputGroup className="dark:bg-background">
                    <InputGroupAddon>
                      <Mail className="h-4 w-4" />
                    </InputGroupAddon>

                    <InputGroupInput
                      data-test={'email-input'}
                      required
                      type="email"
                      placeholder={t('emailPlaceholder')}
                      {...field}
                    />
                  </InputGroup>
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'password'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <PasswordInput autoComplete="current-password" {...field} />
                </FormControl>

                <FormMessage />

                <div>
                  <Button
                    asChild
                    type={'button'}
                    size={'sm'}
                    variant={'link'}
                    className={'text-xs'}
                  >
                    <Link href={'/auth/password-reset'}>
                      <Trans i18nKey={'auth:passwordForgottenQuestion'} />
                    </Link>
                  </Button>
                </div>
              </FormItem>
            )}
          />
        </div>

        <Button
          data-test="auth-submit-button"
          className={'group w-full'}
          type="submit"
          disabled={loading || redirecting || captchaLoading}
        >
          <If condition={redirecting}>
            <span className={'animate-in fade-in'}>
              <Trans i18nKey={'auth:redirecting'} />
            </span>
          </If>

          <If condition={loading}>
            <span className={'animate-in fade-in'}>
              <Trans i18nKey={'auth:signingIn'} />
            </span>
          </If>

          <If condition={captchaLoading}>
            <span className={'animate-in fade-in'}>
              <Trans i18nKey={'auth:verifyingCaptcha'} />
            </span>
          </If>

          <If condition={!redirecting && !loading && !captchaLoading}>
            <span className={'animate-in fade-in flex items-center'}>
              <Trans i18nKey={'auth:signInWithEmail'} />

              <ArrowRight
                className={
                  'zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4'
                }
              />
            </span>
          </If>
        </Button>
      </form>
    </Form>
  );
}
